import { createStore } from './createStore';
import type { StatusChipState } from '../ui/components/StatusPill';

// Sync status facts for the shell (Owner task book 2026-09-04 Round 2 §B / §AL).
//
// The header shows a status ONLY when it is actionable: Offline (the device has no connection),
// Pending Sync (local changes wait for a connection) or Sync Failed. The normal online state shows
// NOTHING — "local only" is explained under Settings → Sync & Storage, not stamped on every screen.
// This build has no cloud sync engine, so pendingChanges stays 0 and failed stays false; the
// derivation is still real (it reads these facts), never a placeholder promise (D-11 stays deferred).
export interface SyncStatusState {
  pendingChanges: number;
  failed: boolean;
  lastSyncedAt: number | null;
}

export const syncStatusStore = createStore<SyncStatusState>({ pendingChanges: 0, failed: false, lastSyncedAt: null });

export function resetSyncStatusForTests(): void {
  syncStatusStore.set(() => ({ pendingChanges: 0, failed: false, lastSyncedAt: null }));
}

export function setSyncStatusForTests(state: Partial<SyncStatusState>): void {
  syncStatusStore.set((prev) => ({ ...prev, ...state }));
}

// null = nothing to show (online, nothing pending, nothing failed).
export function headerStatusFor(online: boolean, sync: SyncStatusState): { state: StatusChipState; count?: number } | null {
  if (!online) return { state: 'offline' };
  if (sync.failed) return { state: 'syncFailed' };
  if (sync.pendingChanges > 0) return { state: 'syncPending', count: sync.pendingChanges };
  return null;
}

// Settings → Sync & Storage shows the fuller truth: offline / pending / failed / local only.
export function storageStatusFor(online: boolean, sync: SyncStatusState): { state: StatusChipState; count?: number } {
  if (!online) return { state: 'offlineSaved' };
  return headerStatusFor(online, sync) ?? { state: 'localOnly' };
}
